import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import ASSETS from "../assets.json";
import { PORTAL_BASE_URL } from "../../../env";
import "./AssetsSection.scss";

const {
    iconArrow:       ICON_ARROW,
    imgWatchBg1:     IMG_WATCH_BG1,
    imgWatchBg2:     IMG_WATCH_BG2,
    imgVideoPreview: IMG_VIDEO,
    imgChair:        IMG_CHAIR,
    imgWatchInput:   IMG_WATCH_INPUT,
    imgSofaInput:    IMG_SOFA_INPUT,
    videoUgc:        VIDEO_UGC,
} = ASSETS.assetsSection;

// ── Card data ─────────────────────────────────────────────────────────────────
const CARDS = [
    {
        tag:         "Lifestyle Image",
        inputImg:    IMG_WATCH_INPUT,
        description: "AI lifestyle image for luxury watch brand",
    },
    {
        tag:         "Video",
        inputImg:    IMG_SOFA_INPUT,
        description: "AI influencer video for large furniture brand",
        isVideo:     true,
    },
    {
        tag:         "Product Variants",
        description: "3D product variants for modern chair collection",
    },
];

const VISIBLE = 3;
const MAX_IDX = CARDS.length - VISIBLE;
const STEP_PX = 440;                    // 400px card + 40px gap

// ── Shared hover overlay — input thumbnail + description + Start Free ─────────
function HoverOverlay({ inputImg, description }) {
    const handleStartFree = (e) => {
        e.stopPropagation();
        window.location.href = `${PORTAL_BASE_URL}/register`;
    };

    return (
        <>
            <div className="lv2-card-hover-fade" />
            <div className="lv2-card-hover-content">
                {inputImg && (
                    <div className="lv2-input-thumb">
                        <img src={inputImg} alt="Input" />
                    </div>
                )}
                <p className="lv2-hover-desc">
                    {description}
                </p>
                <button
                    className="lv2-start-free-btn manrope f-14 w-700 white"
                    onClick={handleStartFree}
                >
                    Start Free
                </button>
            </div>
        </>
    );
}

const AssetsSection = () => {
    const [idx, setIdx] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    const videoRef = useRef(null);
    const history = useHistory();

    const handleVideoEnter = () => {
        if (videoRef.current) videoRef.current.play();
    };

    const handleVideoLeave = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    return (
        <div className="lv2-assets-wrapper">

            {/* ── Heading + nav arrows ─────────────────────────────────────── */}
            <div className="lv2-assets-heading">
                <h2 className="manrope f-38 w-600 lh-46 white">
                    AI generated using ALL3D
                </h2>
                {CARDS.length > VISIBLE && (
                    <div className="lv2-assets-nav">
                        <button
                            className="lv2-nav-btn"
                            onClick={() => setIdx(i => Math.max(0, i - 1))}
                            disabled={idx === 0}
                            aria-label="Previous"
                        >
                            <img src={ICON_ARROW} alt="" />
                        </button>
                        <button
                            className="lv2-nav-btn"
                            onClick={() => setIdx(i => Math.min(MAX_IDX, i + 1))}
                            disabled={idx === MAX_IDX}
                            aria-label="Next"
                        >
                            <img src={ICON_ARROW} alt="" className="lv2-arrow-flip" />
                        </button>
                    </div>
                )}
            </div>

            {/* ── Carousel ─────────────────────────────────────────────────── */}
            <div className="lv2-carousel-outer">
                <div
                    className={`lv2-carousel-track${CARDS.length <= VISIBLE ? " lv2-carousel-track--centered" : ""}`}
                    style={{ transform: `translateX(-${idx * STEP_PX}px)` }}
                >

                    {/* Card 1 — Lifestyle Image */}
                    <div className="lv2-asset-card">
                        <img className="lv2-img lv2-img-watch-1" src={IMG_WATCH_BG1} alt="AI-generated lifestyle image of a luxury watch" />
                        <img className="lv2-img lv2-img-watch-2" src={IMG_WATCH_BG2} alt="AI-generated luxury watch lifestyle image, alternate angle" />
                        <div className="lv2-card-tag">
                            <span className="manrope f-14 w-500 white">{CARDS[0].tag}</span>
                        </div>
                        <HoverOverlay inputImg={CARDS[0].inputImg} description={CARDS[0].description} />
                    </div>

                    {/* Card 2 — Video */}
                    <div
                        className="lv2-asset-card"
                        onMouseEnter={handleVideoEnter}
                        onMouseLeave={handleVideoLeave}
                        onClick={() => setFullscreen(true)}
                        style={{ cursor: "pointer" }}
                    >
                        <img className="lv2-img lv2-img-video" src={IMG_VIDEO} alt="AI influencer video preview for large furniture brand" />
                        <video
                            ref={videoRef}
                            className="lv2-video-el"
                            src={VIDEO_UGC}
                            muted
                            loop
                            playsInline
                        />
                        <div className="lv2-play-icon">
                            <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                                <polygon points="6,3 20,12 6,21" />
                            </svg>
                        </div>
                        <div className="lv2-card-tag">
                            <span className="manrope f-14 w-500 white">{CARDS[1].tag}</span>
                        </div>
                        <HoverOverlay inputImg={CARDS[1].inputImg} description={CARDS[1].description} />
                    </div>

                    {/* Card 3 — Product Variants */}
                    <div className="lv2-asset-card lv2-card-white">
                        <div className="lv2-product-img-wrap">
                            <img src={IMG_CHAIR} alt="3D product variants of a modern chair collection" />
                        </div>
                        <div className="lv2-card-tag">
                            <span className="manrope f-14 w-500 white">{CARDS[2].tag}</span>
                        </div>
                        <HoverOverlay description={CARDS[2].description} />
                    </div>


                </div>
            </div>

            {/* ── Info row ─────────────────────────────────────────────────── */}
            <div className="lv2-assets-info">
                <h3 className="manrope f-24 w-600 lh-32 white lv2-info-question">
                    What are AI-generated product assets?
                </h3>
                <p className="manrope f-16 w-400 lh-24 white lv2-info-body">
                    AI-generated product assets use machine learning models and structured workflows to generate realistic product images, videos, and variations without traditional photography. Brands can create studio shots, lifestyle scenes, and marketing visuals instantly using a combination of prompts, reference images, and product data.
                </p>
                <button
                    className="lv2-gallery-btn manrope f-16 w-500 white"
                    onClick={() => history.push("/ai-images")}
                >
                    View Full Gallery
                </button>
            </div>

            {/* ── Fullscreen video modal ────────────────────────────────────── */}
            {fullscreen && (
                <div className="lv2-fullscreen-overlay" onClick={() => setFullscreen(false)}>
                    <video
                        src={VIDEO_UGC}
                        autoPlay
                        controls
                        className="lv2-fullscreen-video"
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}

        </div>
    );
};

export default AssetsSection;
